<script>
import { mapActions } from 'vuex';

export default {
    data() {
        return {
            roles: [],
            filteredRoles: [],
            
            userPermissions: [],
            assignedUserPermissions: [],

            selectedRoleID: 99,

            companies: [],

            companyPermissions: [],
            assignedCompanyPermissions: [],

            selectedCompanyID: null,

            userCompany: null,
            activeTab: "user",

            isAdminCompany: false,
            loading: false,
        }
    },

    async mounted() {
        this.userCompany = parseInt(this.$user.company_id);
        await this._getCompanyRole();

        this.loading = true;
        await this._getRoles();
        await this._getUserPermissions();
        await this._getAssignedUserPermissionsByCompany();

        if (this.isAdminCompany) {
            await this._getCompanies();
            await this._getCompanyPermissions();
            await this._getAssignedCompanyPermissionsByCompany();
        }
    },

    methods: {
        // initalize //

        ...mapActions("settings", [
            "getRoles",
            "getCompanies"
        ]),

        ...mapActions("company", [
            "getCompaniesCascade"
        ]),

        async _getRoles() {
            this.roles = [];
            const params = {
                role_id: this.$user.role_id
            }
            
            await this.getRoles(params)
                .then( res => {
                    for (let index = 0; index < res.data.length; index++) {
                        const element = res.data[index];

                        if (element.role_id > 1 && element.role_id < 6)
                            this.roles.push(element);
                    }
                })
                .catch(error => {
                    throw new Error(error);
                })
        },

        async selectHighestRoleOnStart() {
            for (let index = 0; index < this.roles.length; index++) {
                if (this.roles[index].role_id < this.selectedRoleID) {
                    this.selectedRoleID = this.roles[index].role_id;
                }
            }
        },

        async _getCompanyRole() {
            await this.getCompaniesCascade()
                .then( res => {
                    const role = res.data.layer_1.find( item => item.role === "god");

                    if (role) 
                        this.isAdminCompany = true;
                })
        },

        async _getCompanies(){
          let params = {
            is_deleted: false,
            user_id: parseInt(this.$user.user_id),
            role_id: parseInt(this.$user.role_id)
          };
          
          await this.getCompanies(params)
            .then(res => {
              res.data.forEach(element => {
                if (this.userCompany != element.company_id) {
                    this.companies.push(
                      {
                        company_name: element.company_name,
                        company_id: element.company_id,
                      }
                    );
                }
              });
              this.companies = this.$sortNumerically(this.companies, "company_id", true);
            })
            .catch(err => {
              console.log(err);
            })
            .finally(() => {
                this.selectedCompanyID = this.companies[0].company_id;
            })
        },

        ...mapActions("admin", [
            /* user permissions */
            "getUserPermissions",
            /* assigned user permissions */
            "getAssignedUserPermissions",
            "getAssignedUserPermissionsByUserID",
            "createAssignedUserPermission",
            "updateAssignedUserPermission",
            "deleteAssignedUserPermission",
            "getAssignedUserPermissionsByCompany",
            "updateAssignedUserPermissionByRole",
            /* company permissions */
            "getCompanyPermissions",
            /* assigned company permissions */
            "getAssignedCompanyPermissionsByCompany",
            "createAssignedCompanyPermission",
            "deleteAssignedCompanyPermission"
        ]),

        async _getUserPermissions() {
            const params = {
                is_deleted: false
            };

            await this.getUserPermissions(params)
                .then( res => {
                    this.userPermissions = res.data;
                })
                .catch(error => {
                    throw new Error(error);
                })
        },

        async _getAssignedUserPermissionsByCompany() {
            const params = {
                company_id: parseInt(this.$user.company_id),
                is_deleted: false
            };

            await this.getAssignedUserPermissionsByCompany(params)
                .then( res => {
                    this.roles = this.roles.filter(item => res.data.some( data => data.role_id == item.role_id));

                    this.assignedUserPermissions = res.data;
                })
                .catch(error => {
                    throw new Error(error);
                })
                .finally(async () => {
                    if (this.roles.length > 0) {
                        await this.selectHighestRoleOnStart();
                    }

                    this.loading = false;
                })
        },

        async _updateAssignedUserPermissionByRole(item) {
            const authorized = this.assignedUserPermissions.find( permission => permission.user_permission_id == item.user_permission_id).is_authorized;

            const params = {
                company_id: parseInt(this.$user.company_id),
                role_id: this.selectedRoleID,
                user_permission_id: item.user_permission_id,
                is_authorized: !authorized,
                updated_by: parseInt(this.$user.user_id)
            }

            await this.updateAssignedUserPermissionByRole(params)
                .then( res => {
                  this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    duration: 1000,
                    dismissible: true
                  })
                })
                .catch(({ response }) => {
                  this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: "error",
                    duration: 5000,
                    dismissible: true,
                  });
                })
                .finally( async () => {
                    await this.onUserUpdate();
                })
        },

        async onUserUpdate() {
            this.loading = true;
            await this._getRoles();
            await this._getUserPermissions();
            await this._getAssignedUserPermissionsByCompany();
        },

        async _getCompanyPermissions() {
            this.loading = true;
            const params = {
                is_deleted: false
            };

            await this.getCompanyPermissions(params)
                .then( res => {
                    this.companyPermissions = res.data;
                })
                .catch(error => {
                    throw new Error(error);
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        async _getAssignedCompanyPermissionsByCompany() {
            this.loading = true;
            const params = {
                company_id: this.selectedCompanyID,
                is_deleted: false
            };

            await this.getAssignedCompanyPermissionsByCompany(params)
                .then( res => {
                    this.assignedCompanyPermissions = res.data;
                })
                .catch(error => {
                    throw new Error(error);
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        async _createAssignedCompanyPermission(item) {
            const params = {
                company_id: this.selectedCompanyID,
                company_permission_id: item.company_permission_id,
                created_by: parseInt(this.$user.user_id)
            }

            await this.createAssignedCompanyPermission(params)
                .then( res => {
                  this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    duration: 1000,
                    dismissible: true
                  })
                })
                .catch(({ response }) => {
                  this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: "error",
                    duration: 5000,
                    dismissible: true,
                  });
                })
                .finally( async () => {
                    await this.onCompanyUpdate();
                })
        },
        async _deleteAssignedCompanyPermission(item) {
            const assignedCompanyPermission = this.assignedCompanyPermissions.find( permission => permission.company_permission_id == item.company_permission_id);

            const params = {
                assigned_company_permission_id: assignedCompanyPermission.assigned_company_permission_id
            }

            await this.deleteAssignedCompanyPermission(params)
                .then( res => {
                  this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    duration: 1000,
                    dismissible: true
                  })
                })
                .catch(({ response }) => {
                  this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: "error",
                    duration: 5000,
                    dismissible: true,
                  });
                })
                .finally( async () => {
                    await this.onCompanyUpdate();
                })
        },

        async onCompanyUpdate() {
            await this._getAssignedCompanyPermissionsByCompany();
        },


        // event handlers //

        selectRole(item) {
            if (item.role_id != this.selectedRoleID) {
                
                this.loading = true;
                this.selectedRoleID = item.role_id;
                
                setTimeout(() => {
                    this.loading = false;
                }, 400);
            }
        },

        selectTab(tab) {
            if (this.activeTab != tab)
                this.activeTab = tab;
        },

        clickSwitchUser(event, item, element) {
            this._updateAssignedUserPermissionByRole(item);

            if (element === "switch") {
                const style = window.getComputedStyle(event.target.children[0]);
                
                if (style.left == "3px") {
                    event.target.children[0].style.left = "37px";
                } 
                if (style.left == "37px") {
                    event.target.children[0].style.left = "3px";
                }
            } 
            if (element === "thumb") {
                const style = window.getComputedStyle(event.target);

                if (style.left == "3px") {
                    event.target.style.left = "37px";
                } 
                if (style.left == "37px") {
                    event.target.style.left = "3px";
                }
            }
        },

        async clickSwitchCompany(event, item, element) {
            if (this.isPermissionTrue(item, 'company')) {
                await this._deleteAssignedCompanyPermission(item);
            } else {
                await this._createAssignedCompanyPermission(item);
            }

            if (element === "switch") {
                const style = window.getComputedStyle(event.target.children[0]);
                
                if (style.left == "3px") {
                    event.target.children[0].style.left = "37px";
                } 
                if (style.left == "37px") {
                    event.target.children[0].style.left = "3px";
                }
            } 
            if (element === "thumb") {
                const style = window.getComputedStyle(event.target);

                if (style.left == "3px") {
                    event.target.style.left = "37px";
                } 
                if (style.left == "37px") {
                    event.target.style.left = "3px";
                }
            }
        },

        isPermissionTrue(item, type) {
            if (type === "user") {
                const permission = this.assignedUserPermissions.find( permission => permission.user_permission_id === item.user_permission_id && permission.role_id === this.selectedRoleID);
                if (permission) {
                    return permission.is_authorized;
                } else {
                    return false;
                }
            } else if (type === "company") {
                const permission = this.assignedCompanyPermissions.find( permission => permission.company_permission_id === item.company_permission_id);
                return (typeof permission != "undefined")
            } else {
                throw new Error("Invalid permission type! ( isPermissionTrue )");
            }

        }

    }
}
</script>

<template>
    <div class="permissions">
        <div class="tabs-container" v-if="true"><!-- isAdminCompany -->
            <div class="tab" :class="{'selected-tab': activeTab === 'user'}" @click="selectTab('user')">{{ $t('permissions.user') }}</div>
            <div class="tab" :class="{'selected-tab': activeTab === 'company'}" @click="selectTab('company')">{{ $t('permissions.company') }}</div>
        </div>

        <div class="flex-all-centered-col page-container" v-if="activeTab === 'user'">
            <div class="flex-all-centered-row role-selector" v-if="!loading">
                <div v-for="item in roles" :key="item.role_id" class="role" :class="{'selected-role': item.role_id == selectedRoleID}" @click="selectRole(item)">
                    {{ item.role_name }}
                </div>
            </div>
            <div class="flex-all-centered-col permissions" v-if="!loading">
                <div v-for="(item, index) in userPermissions" :key="index" class="flex-all-centered-row permission">
                    <div class="flex-all-centered-col permission-details">
                        <div class="permission-name">
                            {{ item.user_permission_name }}
                        </div>
                        <div class="permission-description">
                            {{ item.description }}
                        </div>
                    </div>
                    <div class="permission-switch" @click="clickSwitchUser($event, item, 'switch')" :class="{'permission-switch--authorized': isPermissionTrue(item, 'user'), 'permission-switch--unauthorized': !isPermissionTrue(item, 'user')}">
                        <div class="switch-thumb" @click.stop="clickSwitchUser($event, item, 'thumb')"></div>
                    </div>
                </div>
            </div>
            <div v-else style="min-height: 200px;" class="flex-all-centered-row">
                <b-spinner variant="success" label="Loading..."></b-spinner>
            </div>
        </div>

        <div class="flex-all-centered-col page-container" v-if="activeTab === 'company'"> <!-- change this completely, just a placeholder for now -->

            <select id="company-select" style="align-self: flex-start; margin-left: 1em;" v-model="selectedCompanyID" @change="_getAssignedCompanyPermissionsByCompany()" > 
                <option v-for="item in companies" :key="item.company_id" :value="item.company_id" >
                    {{ item.company_name }}
                </option>
            </select>

            <div class="flex-all-centered-col permissions" v-if="!loading">
                <div v-for="(item, index) in companyPermissions" :key="index" class="flex-all-centered-row permission">
                    <div class="flex-all-centered-col permission-details">
                        <div class="permission-name">
                            {{ item.company_permission_name }}
                        </div>
                        <div class="permission-description">
                            {{ item.description }}
                        </div>
                    </div>
                    <div class="permission-switch" @click="clickSwitchCompany($event, item, 'switch')" :class="{'permission-switch--authorized': isPermissionTrue(item, 'company'), 'permission-switch--unauthorized': !isPermissionTrue(item, 'company')}">
                        <div class="switch-thumb" @click.stop="clickSwitchCompany($event, item, 'thumb')"></div>
                    </div>
                </div>
            </div>
            <div v-else style="min-height: 200px;" class="flex-all-centered-row">
                <b-spinner variant="success" label="Loading..."></b-spinner>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>

    .permissions {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        overflow-y: auto;
    }

    .tabs-container {
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;

        border-radius: 1.5em;
        border: thin solid var(--sustainfinity-navy);

        margin-bottom: 1em;

        overflow: hidden;

        > .tab {
            text-align: center;
            width: 100%;

            color: var(--sustainfinity-navy);
            background-color: whitesmoke;

            cursor: pointer;
            transition: background-color 250ms;

            &:hover {
                background-color: lightgray;
            }
        }

        > .selected-tab {
            background-color: var(--sustainfinity-navy) !important;
            color: whitesmoke !important;
            pointer-events: none !important;
        }
    }

    .page-container {
        background-color: whitesmoke;

        padding: 1.33em;
        border-radius: 1.5em;
        box-shadow: 0 .1em .5em .25em gray;
        width: 100%;

        > .role-selector {

            padding-left: 1em;
            justify-content: flex-start;
            filter: drop-shadow(0 .33em .33em gray);

            > .role {
                padding: 1em 2em .75em 2em;
                background-color: var(--sustainfinity-navy);

                border-top-left-radius: 1em;
                border-top-right-radius: 1em;
                border-bottom: .15em solid var(--sustainfinity-navy);

                color: white;
                font-weight: bold;

                transition: all 100ms;
                cursor: pointer;
                &:hover {
                    background-color: whitesmoke;
                    color: var(--sustainfinity-navy);
                }
            }
            
            .selected-role {
                background-color: whitesmoke !important;
                color: var(--sustainfinity-navy) !important;
            }

            margin-bottom: .5em;
        }

        > .permissions {
            > .permission {
                border-bottom: thin solid var(--sustainfinity-blue);

                justify-content: flex-start;
                align-content: left;
                align-items: stretch;
                text-align: left;

                padding: 1em;

                > .permission-details {
                    justify-content: space-between;
                    align-content: left;
                    align-items: stretch;
                    text-align: left;

                    > .permission-name {
                        font-size: 1.2em;
                        font-weight: bold;
                        color: var(--sustainfinity-navy);
                    }

                    > .permission-description {
                        font-size: .9em;
                        font-style: italic;
                        color: var(--sustainfinity-navy);
                    }
                }


                > .permission-switch {
                    position: relative;
                    border-radius: 1.5em;
                    box-shadow: 0 0 .33em .1em gray;
                    width: 80px;
                    
                    transition: all 150ms;
                    cursor: pointer;
                    .switch-thumb {
                        transition: all 150ms;
                        position: absolute;
                        top: 50%;
                        transform: translateY(-50%);
                        width: 34px;
                        height: 34px;
                        border-radius: 20px;
                        background-color: var(--sustainfinity-navy);
                    }
                }

                > .permission-switch--authorized {
                    position: relative;
                    border: thin solid whitesmoke;
                    background-color: var(--sustainfinity-green);

                    .switch-thumb {
                        left: 37px;
                        background-color: whitesmoke;
                    }
                }

                > .permission-switch--unauthorized {
                    position: relative;
                    border: thin solid var(--sustainfinity-navy);
                    background-color: whitesmoke;

                    .switch-thumb {
                        left: 3px;
                        background-color: var(--sustainfinity-navy);
                    }
                }

            }
        }
    }
</style>